import { ensureRunsOnce } from '../helpers/singletons/ensureRunsOnce';
import { useLayoutEffect } from 'react';
import CustomerDataCustomer from 'mage-swagfaces/customer/CustomerDataCustomer';
import Itly from 'itly';
import { LoadingState } from 'store/constants';

const DEFAULT_DELAY_MS = 0;
const DEFAULT_SENDESK_KEY = process.env.GATSBY_SENDESK_KEY;
const SENDESK_SCRIPT_ID = 'ze-snippet';

interface UseZendeskProps {
  key?: string;
  delay?: number;
  debug?: boolean;
  customer: CustomerDataCustomer;
  customerLoadingState: LoadingState;
}

export const useZendesk = ({
  key = DEFAULT_SENDESK_KEY,
  delay = DEFAULT_DELAY_MS,
  debug,
  customer,
  customerLoadingState
}: UseZendeskProps): void => {
  useLayoutEffect(() => {
    if (!document.getElementById(SENDESK_SCRIPT_ID) && customer && customerLoadingState === LoadingState.SUCCESS) {
      ensureRunsOnce(async () => {
        const e = document.createElement('script');
        e.type = 'text/javascript';
        e.async = true;
        e.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;
        e.id = SENDESK_SCRIPT_ID;
        // Adding onload/onerror handlers before inserting <script> into the document.
        const loadScript = new Promise<void>((resolve, reject) => {
          e.onload = () => resolve();
          e.onerror = reject;
        });

        debug && console.debug(`[useZendesk()] Scheduling add <script> in ${delay}ms...`);

        setTimeout(() => {
          // Finally, add <script> to the document.
          const t = document.getElementsByTagName('script')[0];
          t.parentNode.insertBefore(e, t);
          debug && console.debug('[useZendesk()] ...<script> added!');
          console.log('[useZendesk()] Zendesk script loaded');
        }, delay);

        await loadScript;

        //wait for zendesk to be available
        const waitForZendesk = (): Promise<typeof zE> =>
          new Promise((resolve) => {
            const interval = setInterval(() => {
              if (typeof window !== 'undefined' && typeof window.zE === 'function') {
                clearInterval(interval);
                resolve(window.zE);
              }
            }, 100);
          });

        const zenDesk = await waitForZendesk();

        const id = customer.id || 0;
        const email = customer.email || '';

        zenDesk('webWidget:on', 'open', () => {
          Itly.chatOpened({ email: email, user_id: id });
        });

        zenDesk('webWidget:on', 'chat:start', () => {
          Itly.chatStarted({ email: email, user_id: id });
        });
      })();
    }

    // TODO: Refactor this dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, customerLoadingState]);
};
